import React from "react";
import { Link } from "react-router-dom"
import './SiteLink.scss';
import ExternalLink from "./ExternalLink";

export type SiteLinkProps = {
    children?: React.ReactNode;
    onClick?: () => void;
    className?: string;
    external?: boolean;
    hideExternalIcon?: boolean;
}

const UrlResolver = {

    forDashboard(): string {
        return "/";
    },

    forOfferList(offerStatus: string): string {
        return `/offers/${offerStatus.toLowerCase()}`;
    },

    forOfferDetails(offerDisplayId: string): string {
        return `/offers/${offerDisplayId}/details`;
    },

    forHedgeDetails(positionHedgeId: number | string): string {
        return `/hedges/${positionHedgeId}/details`;
    },

    forContractDetails(contractDisplayId: string): string {
        return `/contracts/${contractDisplayId}/details`;
    },

    forLocationSettings(): string {
        return '/settings/locations';
    },

    forCommoditySettings(): string {
        return '/settings/commodities';
    },

    forHedgingSettings(): string {
        return '/settings/hedgings';
    },

    forNotifications(): string {
        return '/notifications';
    },

    forBidEditor(): string {
        return '/bid-editor';
    },

    forBidEditorCommodity(commodityId: number): string {
        return `/bid-editor/${commodityId}`;
    },

    forLongShortSettings(): string {
        return '/settings/long-short';
    },

    forApiKeySettings(): string {
        return '/settings/integration';
    },

    forDisclaimer(): string {
        return '/disclaimer';
    },

    forSupportTicket(): string {
        return '/support-ticket';
    },

    forTroubleshooting(): string {
        return '/troubleshooting';
    },

    forDocuments(): string {
        return '/documents';
    },

    forEsign(): string {
        return '/reports/esign';
    },

    forHedges(): string {
        return '/hedges';
    }
}

const BaseSiteLink: React.FC<SiteLinkProps & { to: string }> = props => {

    return props.external
        ? <ExternalLink url={props.to} className={props.className} hideExternalIcon={props.hideExternalIcon}>{props.children}</ExternalLink>
        : <Link to={props.to} className={props.className}>{props.children}</Link>
}

const Dashboard = React.memo((props: SiteLinkProps) => <BaseSiteLink to={UrlResolver.forDashboard()} {...props} />)
const OffersList = React.memo((props: { offerStatus: string } & SiteLinkProps) => <BaseSiteLink to={UrlResolver.forOfferList(props.offerStatus)}>{props.offerStatus} Offers</BaseSiteLink>);
const OfferDetails = React.memo((props: { offerDisplayId: string } & SiteLinkProps) => <BaseSiteLink to={UrlResolver.forOfferDetails(props.offerDisplayId)} {...props}>{props.children ?? props.offerDisplayId}</BaseSiteLink>);
const ContractDetails = React.memo((props: { contractDisplayId: string } & SiteLinkProps) => <BaseSiteLink to={UrlResolver.forContractDetails(props.contractDisplayId)} {...props}>{props.children ?? props.contractDisplayId}</BaseSiteLink>);
const HedgeDetails = React.memo((props: { positionHedgeId: string | number } & SiteLinkProps) => <BaseSiteLink to={UrlResolver.forHedgeDetails(props.positionHedgeId)} {...props}>{props.children ?? props.positionHedgeId}</BaseSiteLink>);
const SettingsLocations = React.memo((props: SiteLinkProps) => <BaseSiteLink to={UrlResolver.forLocationSettings()} {...props} />);
const SettingsCommodities = React.memo((props: SiteLinkProps) => <BaseSiteLink to={UrlResolver.forCommoditySettings()} {...props} />);
const SettingsHedgings = React.memo((props: SiteLinkProps) => <BaseSiteLink to={UrlResolver.forHedgingSettings()} {...props} />);
const Notifications = React.memo((props: SiteLinkProps) => <BaseSiteLink to={UrlResolver.forNotifications()} {...props} />)
const BidEditor = React.memo((props: SiteLinkProps) => <BaseSiteLink to={UrlResolver.forBidEditor()} {...props} />)
const BidEditorCommodity = React.memo((props: { commodityId: number } & SiteLinkProps) => <BaseSiteLink to={UrlResolver.forBidEditorCommodity(props.commodityId)} {...props} />)
const SettingsLongShort = React.memo((props: SiteLinkProps) => <BaseSiteLink to={UrlResolver.forLongShortSettings()} {...props} />);
const SettingsApiKeys = React.memo((props: SiteLinkProps) => <BaseSiteLink to={UrlResolver.forApiKeySettings()} {...props} />)
const Disclaimer = React.memo((props: SiteLinkProps) => <BaseSiteLink to={UrlResolver.forDisclaimer()} {...props} />)
const SupportTicket = React.memo((props: SiteLinkProps) => <BaseSiteLink to={UrlResolver.forSupportTicket()} {...props} />)
const Troubleshooting = React.memo((props: SiteLinkProps) => <BaseSiteLink to={UrlResolver.forTroubleshooting()} {...props} />)
const Documents = React.memo((props: SiteLinkProps) => <BaseSiteLink to={UrlResolver.forDocuments()} {...props} />)
const Hedges = React.memo((props: SiteLinkProps) => <BaseSiteLink to={UrlResolver.forHedges()} {...props} />)


export default {
    BaseSiteLink,
    Dashboard,
    OffersList,
    OfferDetails,
    ContractDetails,
    HedgeDetails,
    SettingsLocations,
    SettingsCommodities,
    SettingsHedgings,
    Notifications,
    BidEditor,
    BidEditorCommodity,
    SettingsLongShort,
    SettingsApiKeys,
    Disclaimer,
    UrlResolver,
    SupportTicket,
    Troubleshooting,
    Documents,
    Hedges,
};